import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  Download,
  FileText,
  FolderKanban,
  Image as ImageIcon,
  Upload,
  Video,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useHasPermission, useMe } from "@/lib/session";

export const Route = createFileRoute("/_app/drive")({
  component: DrivePage,
});

interface ProjectCard {
  id: string;
  name: string;
  avatarR2Key: string | null;
}

interface DocRow {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  kind: string;
  createdAt: number;
  uploader: { kind: "client" | "team"; name: string | null };
}

function DrivePage() {
  const canUpload = useHasPermission("archives.upload");
  const projectsQ = useQuery<{ projects: ProjectCard[] }>({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects"),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-pick el primero al cargar.
  useEffect(() => {
    if (!selectedId && projectsQ.data?.projects?.[0]) {
      setSelectedId(projectsQ.data.projects[0].id);
    }
  }, [projectsQ.data, selectedId]);

  if (projectsQ.isLoading) {
    return (
      <div className="grid place-items-center py-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  const projects = projectsQ.data?.projects ?? [];
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Archivos"
          title="Drive de proyectos"
          subtitle="Compartir evidencia con el equipo."
        />
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Aún no tienes proyectos visibles.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Archivos"
        title="Drive de proyectos"
        subtitle="Sube evidencia (screenshots, PDFs, videos) y descarga lo que el equipo te comparte. Cada proyecto tiene su carpeta."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)] gap-4">
        <aside className="card-elevated p-2 h-fit lg:sticky lg:top-4">
          <ul className="space-y-1">
            {projects.map((p) => {
              const active = p.id === selectedId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    className={
                      "w-full text-left flex items-center gap-2 px-3 py-2 rounded-md transition-colors " +
                      (active
                        ? "bg-primary/15 text-primary font-medium"
                        : "hover:bg-base-200/60")
                    }
                  >
                    {p.avatarR2Key ? (
                      <img
                        src={`/api/projects/${p.id}/avatar`}
                        alt=""
                        className="size-6 rounded object-cover"
                      />
                    ) : (
                      <div className="size-6 rounded bg-primary/15 grid place-items-center">
                        <FolderKanban className="size-3.5 text-primary" />
                      </div>
                    )}
                    <span className="text-sm truncate">{p.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <div>
          {selectedId && <ProjectDrive projectId={selectedId} canUpload={canUpload} />}
        </div>
      </div>
    </div>
  );
}

function ProjectDrive({ projectId, canUpload }: { projectId: string; canUpload: boolean }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const docsQ = useQuery<{ documents: DocRow[] }>({
    queryKey: ["project", projectId, "documents"],
    queryFn: () => api.get(`/projects/${projectId}/documents`),
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.set("file", file);
        await api.post(`/projects/${projectId}/documents`, fd);
      }
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["project", projectId, "documents"] }),
  });

  return (
    <div className="space-y-4">
      {canUpload && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length > 0) upload.mutate(e.dataTransfer.files);
          }}
          className="card-elevated p-6 border-2 border-dashed border-base-300/60 flex flex-col items-center justify-center gap-2 text-center"
        >
          <div className="size-10 rounded-lg bg-primary/15 text-primary grid place-items-center">
            <Upload className="size-5" />
          </div>
          <div className="font-medium text-sm">Arrastra archivos aquí</div>
          <div className="text-xs text-base-content/55">
            o haz click para seleccionar · máx. 25 MB por archivo
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                upload.mutate(e.target.files);
                e.target.value = "";
              }
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="btn btn-primary btn-sm mt-2"
          >
            {upload.isPending && <span className="loading loading-spinner loading-xs" />}
            <Upload className="size-4" /> Subir archivos
          </button>
          {upload.isError && (
            <div className="text-xs text-error mt-2 break-words max-w-md">
              {(upload.error as Error)?.message ?? "Error subiendo el archivo."}
            </div>
          )}
        </div>
      )}

      {docsQ.isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner text-primary" />
        </div>
      ) : !docsQ.data?.documents.length ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Sin archivos en este proyecto todavía.
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docsQ.data.documents.map((d) => (
            <DocCard key={d.id} doc={d} mineName={me?.user?.name ?? null} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DocCard({ doc, mineName }: { doc: DocRow; mineName: string | null }) {
  const isImage = doc.contentType.startsWith("image/");
  const isVideo = doc.contentType.startsWith("video/");
  const url = `/api/documents/${doc.id}/download`;
  const ago = timeAgo(doc.createdAt);
  const fmtSize = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const uploaderLabel =
    doc.uploader.kind === "client"
      ? doc.uploader.name === mineName
        ? "Tú"
        : (doc.uploader.name ?? "Cliente")
      : "Equipo Axorial";
  return (
    <li className="card-elevated overflow-hidden">
      {isImage ? (
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img
            src={url}
            alt={doc.filename}
            className="w-full aspect-video object-cover bg-base-200"
            loading="lazy"
          />
        </a>
      ) : isVideo ? (
        <video
          src={url}
          controls
          preload="metadata"
          className="w-full aspect-video object-contain bg-black"
        />
      ) : (
        <div className="aspect-video bg-base-200 grid place-items-center">
          <FileText className="size-10 text-base-content/40" />
        </div>
      )}
      <div className="p-3 space-y-1">
        <div className="font-medium text-sm truncate" title={doc.filename}>
          {doc.filename}
        </div>
        <div className="text-xs text-base-content/55 flex items-center gap-2 flex-wrap">
          {isImage ? (
            <ImageIcon className="size-3" />
          ) : isVideo ? (
            <Video className="size-3" />
          ) : (
            <FileText className="size-3" />
          )}
          <span>{fmtSize(doc.sizeBytes)}</span>
          <span>·</span>
          <span>{ago}</span>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span
            className={
              "badge badge-xs " +
              (doc.uploader.kind === "client" ? "badge-primary" : "badge-ghost")
            }
          >
            {uploaderLabel}
          </span>
          <a href={url} download={doc.filename} className="btn btn-ghost btn-xs gap-1">
            <Download className="size-3" /> Descargar
          </a>
        </div>
      </div>
    </li>
  );
}

function timeAgo(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}
